export interface Quote {
  text: string;
  author: string;
}

interface CachedQuote {
  quote: Quote;
  date: string;
}

const STORAGE_KEY = 'symphonytodo-quote-of-the-day';

export const quoteService = {
  /**
   * Gets the quote of the day, using cache if available and still valid for today
   */
  getQuoteOfTheDay(): Quote {
    // Check if we have a cached quote for today
    const cached = this.getCachedQuote();
    if (cached && this.isToday(cached.date)) {
      return cached.quote;
    }

    // Get a new random quote from our static collection
    // Use date as seed for consistent "daily" quote
    const today = new Date().toDateString();
    const dateHash = this.hashString(today);
    const quoteIndex = Math.abs(dateHash) % 50; // Use first 50 quotes for consistency
    
    // Import quotes directly since we can't use the helper function with seeded random
    const quotes = [
      { text: "The only way to do great work is to love what you do.", author: "Steve Jobs" },
      { text: "Life is what happens to you while you're busy making other plans.", author: "John Lennon" },
      { text: "The future belongs to those who believe in the beauty of their dreams.", author: "Eleanor Roosevelt" },
      { text: "It is during our darkest moments that we must focus to see the light.", author: "Aristotle" },
      { text: "The only impossible journey is the one you never begin.", author: "Tony Robbins" },
      { text: "In the end, we will remember not the words of our enemies, but the silence of our friends.", author: "Martin Luther King Jr." },
      { text: "The best time to plant a tree was 20 years ago. The second best time is now.", author: "Chinese Proverb" },
      { text: "Your limitation—it's only your imagination.", author: "Unknown" },
      { text: "Push yourself, because no one else is going to do it for you.", author: "Unknown" },
      { text: "Great things never come from comfort zones.", author: "Unknown" },
      { text: "Dream it. Wish it. Do it.", author: "Unknown" },
      { text: "Success doesn't just find you. You have to go out and get it.", author: "Unknown" },
      { text: "The harder you work for something, the greater you'll feel when you achieve it.", author: "Unknown" },
      { text: "Dream bigger. Do bigger.", author: "Unknown" },
      { text: "Don't stop when you're tired. Stop when you're done.", author: "Unknown" },
      { text: "Wake up with determination. Go to bed with satisfaction.", author: "Unknown" },
      { text: "Do something today that your future self will thank you for.", author: "Sean Patrick Flanery" },
      { text: "Little things make big days.", author: "Unknown" },
      { text: "It's going to be hard, but hard does not mean impossible.", author: "Unknown" },
      { text: "Don't wait for opportunity. Create it.", author: "Unknown" },
      { text: "Sometimes we're tested not to show our weaknesses, but to discover our strengths.", author: "Unknown" },
      { text: "The key to success is to focus on goals, not obstacles.", author: "Unknown" },
      { text: "Dream it. Believe it. Build it.", author: "Unknown" },
      { text: "What we think, we become.", author: "Buddha" },
      { text: "Whether you think you can or you think you can't, you're right.", author: "Henry Ford" },
      { text: "The only person you are destined to become is the person you decide to be.", author: "Ralph Waldo Emerson" },
      { text: "Believe you can and you're halfway there.", author: "Theodore Roosevelt" },
      { text: "I have learned over the years that when one's mind is made up, this diminishes fear.", author: "Rosa Parks" },
      { text: "It does not matter how slowly you go as long as you do not stop.", author: "Confucius" },
      { text: "Everything you've ever wanted is on the other side of fear.", author: "George Addair" },
      { text: "Success is not final, failure is not fatal: it is the courage to continue that counts.", author: "Winston Churchill" },
      { text: "The way to get started is to quit talking and begin doing.", author: "Walt Disney" },
      { text: "Don't be afraid to give up the good to go for the great.", author: "John D. Rockefeller" },
      { text: "I find that the harder I work, the more luck I seem to have.", author: "Thomas Jefferson" },
      { text: "Don't be afraid to stand for what you believe in, even if that means standing alone.", author: "Unknown" },
      { text: "Challenges are what make life interesting and overcoming them is what makes life meaningful.", author: "Joshua J. Marine" },
      { text: "If you want to lift yourself up, lift up someone else.", author: "Booker T. Washington" },
      { text: "A person who never made a mistake never tried anything new.", author: "Albert Einstein" },
      { text: "The person who says it cannot be done should not interrupt the person who is doing it.", author: "Chinese Proverb" },
      { text: "There are no traffic jams along the extra mile.", author: "Roger Staubach" },
      { text: "It is never too late to be what you might have been.", author: "George Eliot" },
      { text: "You become what you believe.", author: "Oprah Winfrey" },
      { text: "I would rather die of passion than of boredom.", author: "Vincent van Gogh" },
      { text: "A truly rich man is one whose children run into his arms when his hands are empty.", author: "Unknown" },
      { text: "Build your own dreams, or someone else will hire you to build theirs.", author: "Farrah Gray" },
      { text: "The battles that count aren't the ones for gold medals. The struggles within yourself–the invisible battles inside all of us–that's where it's at.", author: "Jesse Owens" },
      { text: "Education costs money. But then so does ignorance.", author: "Sir Claus Moser" },
      { text: "I have learned that people will forget what you said, people will forget what you did, but people will never forget how you made them feel.", author: "Maya Angelou" },
      { text: "A lot of people are afraid to say what they want. That's why they don't get what they want.", author: "Madonna" },
      { text: "Believe you can and you're halfway there.", author: "Theodore Roosevelt" }
    ];
    
    const selectedQuote = quotes[quoteIndex];
    this.cacheQuote(selectedQuote);
    return selectedQuote;
  },

  /**
   * Gets cached quote from localStorage
   */
  getCachedQuote(): CachedQuote | null {
    try {
      const cached = localStorage.getItem(STORAGE_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      console.error('Failed to parse cached quote:', error);
      return null;
    }
  },

  /**
   * Caches quote with today's date
   */
  cacheQuote(quote: Quote): void {
    try {
      const cached: CachedQuote = {
        quote,
        date: new Date().toDateString()
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cached));
    } catch (error) {
      console.error('Failed to cache quote:', error);
    }
  },

  /**
   * Checks if the given date string is today
   */
  isToday(dateString: string): boolean {
    return dateString === new Date().toDateString();
  },

  /**
   * Simple hash function to convert string to number for consistent daily quotes
   */
  hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash;
  }
};